/** @format */

import axios from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import {
    ISandboxCircle,
    ICreateSandboxCirclePayload,
    SandboxContributionStatus,
    SandboxDayStatus,
    ISandboxShareResult,
    ISandboxConvertResult,
} from "../../utility/types/sandboxTypes";

// Sandbox routes are public (no withCredentials required for most calls)

/** POST /sandbox/circles/quick-demo */
export const submitCreateQuickDemoCircle = async (
    anonymousSessionId?: string | null,
    opts?: { country?: string; currency?: string },
): Promise<ISandboxCircle> => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/sandbox/circles/quick-demo`,
            { anonymousSessionId: anonymousSessionId ?? null, ...opts },
        );
        return response.data.data;
    } catch (error: any) {
        const errorMessage =
            error.response?.data?.message || "Failed to create demo circle.";
        throw new Error(errorMessage);
    }
};

/** POST /sandbox/circles */
export const submitCreateCustomSandboxCircle = async (
    payload: ICreateSandboxCirclePayload,
): Promise<ISandboxCircle> => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/sandbox/circles`,
            payload,
        );
        return response.data.data;
    } catch (error: any) {
        const errorMessage =
            error.response?.data?.message || "Failed to create sandbox circle.";
        throw new Error(errorMessage);
    }
};

/** GET /sandbox/circles/:sandboxCircleId */
export const submitGetSandboxCircle = async (
    sandboxCircleId: string,
): Promise<ISandboxCircle> => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/sandbox/circles/${sandboxCircleId}`,
        );
        return response.data.data;
    } catch (error: any) {
        const errorMessage =
            error.response?.data?.message || "Failed to fetch sandbox circle.";
        throw new Error(errorMessage);
    }
};

/** GET /sandbox/circles/share/:shareToken */
export const submitGetSandboxCircleByToken = async (
    shareToken: string,
): Promise<ISandboxCircle> => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/sandbox/circles/share/${shareToken}`,
        );
        return response.data.data;
    } catch (error: any) {
        const errorMessage =
            error.response?.data?.message ||
            "This sandbox preview is no longer available.";
        throw new Error(errorMessage);
    }
};

/** PATCH /sandbox/circles/:sandboxCircleId/members/:memberId/status */
export const submitUpdateMemberStatus = async (
    sandboxCircleId: string,
    memberId: string,
    contributionStatus: SandboxContributionStatus,
): Promise<ISandboxCircle> => {
    try {
        const response = await axios.patch(
            `${API_BASE_URL}/sandbox/circles/${sandboxCircleId}/members/${memberId}/status`,
            { contributionStatus },
        );
        return response.data.data;
    } catch (error: any) {
        const errorMessage =
            error.response?.data?.message || "Failed to update member status.";
        throw new Error(errorMessage);
    }
};

/** PATCH /sandbox/circles/:sandboxCircleId/members/:memberId/days/:date */
export const submitUpdateMemberDayStatus = async (
    sandboxCircleId: string,
    memberId: string,
    date: string,
    dayStatus: SandboxDayStatus,
): Promise<ISandboxCircle> => {
    try {
        const response = await axios.patch(
            `${API_BASE_URL}/sandbox/circles/${sandboxCircleId}/members/${memberId}/days/${date}`,
            { dayStatus },
        );
        return response.data.data;
    } catch (error: any) {
        const errorMessage =
            error.response?.data?.message || "Failed to update contribution day.";
        throw new Error(errorMessage);
    }
};

/** POST /sandbox/circles/:sandboxCircleId/share */
export const submitCreateShareLink = async (
    sandboxCircleId: string,
): Promise<ISandboxShareResult> => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/sandbox/circles/${sandboxCircleId}/share`,
        );
        return response.data.data;
    } catch (error: any) {
        const errorMessage =
            error.response?.data?.message || "Failed to generate share link.";
        throw new Error(errorMessage);
    }
};

/** POST /sandbox/circles/:sandboxCircleId/convert (requires auth cookie) */
export const submitConvertSandboxCircle = async (
    sandboxCircleId: string,
): Promise<ISandboxConvertResult> => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/sandbox/circles/${sandboxCircleId}/convert`,
            {},
            { withCredentials: true },
        );
        return response.data.data;
    } catch (error: any) {
        const errorMessage =
            error.response?.data?.message ||
            "Failed to convert sandbox circle. Please sign in and try again.";
        throw new Error(errorMessage);
    }
};
